import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { commercialCyclesApi, CycleRunProgressDto, GenerateDraftRun } from "@/lib/api";
import { CYCLE_QUERY_KEYS } from "./constants";

const RUN_POLL_INTERVAL_MS = 15000;
const PROGRESS_POLL_INTERVAL_MS = 1500;
const STORAGE_KEY = "commercial-cycles:active-generate-draft-run";

export interface ActiveGenerateDraftRun {
  runId: number;
  sourceYear: number;
  targetYear: number;
}

/**
 * Persiste o runId ativo em localStorage — a barra de progresso continua visível mesmo
 * se o modal for fechado, a página recarregada, ou o admin sair e voltar pra tela.
 */
export function useActiveGenerateDraftRun() {
  const [active, setActiveState] = useState<ActiveGenerateDraftRun | null>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as ActiveGenerateDraftRun) : null;
    } catch {
      return null;
    }
  });

  const setActive = (value: ActiveGenerateDraftRun | null) => {
    setActiveState(value);
    try {
      if (value) localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      // localStorage indisponível (modo privado, etc.) — segue só em memória.
    }
  };

  return [active, setActive] as const;
}

/**
 * Faz polling de GET /admin/cycles/generate-draft/runs/{runId} — fonte da verdade do
 * resultado final. Para automaticamente assim que o status sai de "Running".
 */
export function useGenerateDraftRun(runId: number | null): GenerateDraftRun | null {
  const { data } = useQuery<GenerateDraftRun | null>({
    queryKey: runId ? CYCLE_QUERY_KEYS.generateDraftRun(runId) : ["generate-draft-run", null],
    queryFn: () =>
      runId ? commercialCyclesApi.getGenerateDraftRun(runId) : Promise.resolve(null),
    enabled: !!runId,
    refetchInterval: (query) =>
      query.state.data?.status === "Running" ? RUN_POLL_INTERVAL_MS : false,
  });

  return data ?? null;
}

/**
 * Faz polling de GET /admin/cycles/generate-draft/runs/{runId}/progress a cada 1,5 s.
 * Para automaticamente quando a própria resposta retornar status !== "Running"
 * (Completed ou Failed) — sem depender do poll de 15s do run principal.
 * Retorna os dados mesmo após concluído, para que o pai possa usar progress.status
 * como sinal antecipado de término.
 */
export function useGenerateDraftRunProgress(
  runId: number | null,
  isRunning: boolean,
): CycleRunProgressDto | null {
  const { data } = useQuery<CycleRunProgressDto | null>({
    queryKey: runId
      ? CYCLE_QUERY_KEYS.generateDraftRunProgress(runId)
      : ["generate-draft-run-progress", null],
    queryFn: () =>
      runId
        ? commercialCyclesApi.getGenerateDraftRunProgress(runId)
        : Promise.resolve(null),
    enabled: !!runId && isRunning,
    refetchInterval: (query) =>
      query.state.data?.status === "Running" ? PROGRESS_POLL_INTERVAL_MS : false,
  });

  return data ?? null;
}
